/**
 * writing-style-block — the shared render face for the W4 »Schreibstil« ASSET
 * (`WritingStyleProfile`), the versioned writing-voice artifact the Schreibstil
 * workshop produces.
 *
 * P-A in one file: the WORKSHOP elicits (`career-writing-style`), the KOMPASS holds
 * and renders the result as a readable, consumable document. This module is that
 * render face — ONE implementation, so the Kompass and any later consumer (a share
 * link, another platform app) can never drift into two looks for one asset.
 *
 * Pure lit-html, no ViewModel: takes the resolved `t()` and the already-fetched domain
 * shape, exactly like `werte-summary.ts` (the values asset's face, the direct
 * precedent). Faces are flat document entries (§0 »seamless document«, Genre A) —
 * typography, spacing and hairlines carry the structure; there are no bounding boxes
 * here. Styles live in the Kompass section of `index.html` under `.wsb-*`.
 *
 * OWNERSHIP NOTE: WS-B owns the Kompass surface and its asset-section seam. This file
 * is self-contained on purpose so WS-B can mount it with a single call and WS-D never
 * has to edit a WS-B file.
 */

import { html, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { WritingStyleProfile, WritingStyleRound, WritingStyleTrait } from "@career/definition-v2"
import type { CareerI18n, CareerKey } from "../../i18n"

type Translate = CareerI18n["t"]

/** Round → the German name the trait basis reads out. Named rounds (not numbers) so the
 *  line stays readable if the round order is ever revisited. */
const ROUND_LABEL: Record<WritingStyleRound, CareerKey> = {
  prose: "writing.round.prose",
  scenarios: "writing.round.scenarios",
  validation: "writing.round.validation",
}

/**
 * Confidence as a WORD, not a number. A person reading their own profile cannot act on
 * "0.42", and a bare decimal invites false precision about something derived from three
 * short rounds. The thresholds are deliberately conservative: a profile built from the
 * prose round alone lands in `low` and says so.
 */
function confidenceKey(confidence: number): CareerKey {
  if (confidence < 0.4) return "writing.confidence.low"
  if (confidence < 0.7) return "writing.confidence.medium"
  return "writing.confidence.high"
}

/** One trait row — the axis, the reading, and WHICH rounds it rests on. The basis is
 *  what makes the reading arguable: a trait a person cannot trace back to something
 *  they actually did is a trait they cannot disagree with. */
function traitRow(t: Translate, trait: WritingStyleTrait): TemplateResult {
  const rounds = trait.basis.map((r) => t(ROUND_LABEL[r])).join(", ")
  return html`<li class="wsb-trait doc-row">
    <span class="wsb-trait-axis" lang="de">${trait.axis}</span>
    <span class="wsb-trait-reading" lang="de">${trait.reading}</span>
    ${trait.basis.length > 0
      ? html`<span class="wsb-trait-basis" lang="de">${t("writing.trait.basis", { rounds })}</span>`
      : nothing}
  </li>`
}

/** A plain instruction list (guidance / avoid). Renders nothing when empty rather than
 *  an empty heading — an absent half is silent, never a hollow section. */
function instructionList(t: Translate, titleKey: CareerKey, items: readonly string[], cls: string): TemplateResult | typeof nothing {
  if (items.length === 0) return nothing
  return html`<div class=${`wsb-group ${cls}`}>
    <h4 class="wsb-group-title eyebrow" lang="de">${t(titleKey)}</h4>
    <ul class="wsb-list" role="list">
      ${items.map((line) => html`<li class="wsb-line" lang="de">${line}</li>`)}
    </ul>
  </div>`
}

/**
 * The empty face — an HONEST gap plus the one forward move that closes it. Uses the
 * shared `<career-next-step>` element (R5) rather than a hand-rolled link, so the
 * Kompass's forward navigation stays one implementation.
 */
export function writingStyleEmpty(t: Translate): TemplateResult {
  return html`<div class="wsb-empty">
    <p class="wsb-empty-body" lang="de">${t("writing.block.empty")}</p>
    <career-next-step label=${t("writing.block.emptyCta")} url="/workshops/writing"></career-next-step>
  </div>`
}

/**
 * The Schreibstil asset as a document face.
 *
 * `profile` null ⇒ the honest empty face. Every optional half (traits, guidance, avoid,
 * samples) renders only when populated, so a first-version profile derived from one
 * round reads as a short honest document rather than a form with blank fields.
 */
export function writingStyleBlock(t: Translate, profile: WritingStyleProfile | null): TemplateResult {
  if (!profile) return writingStyleEmpty(t)

  const derived = new Date(profile.derivedAt).toLocaleDateString("de-DE")
  const sample = profile.samples.at(0)

  return html`<div class="wsb">
    <p class="wsb-meta" lang="de">
      ${t("writing.block.version", { version: String(profile.version), date: derived })}
      <span class="wsb-meta-sep" aria-hidden="true">·</span>
      <span class="wsb-confidence" lang="de">${t(confidenceKey(profile.confidence))}</span>
    </p>

    <p class="wsb-summary" lang="de">${profile.summary}</p>

    ${profile.traits.length > 0
      ? html`<div class="wsb-group wsb-traits">
          <h4 class="wsb-group-title eyebrow" lang="de">${t("writing.block.traits")}</h4>
          <ul class="wsb-list" role="list">
            ${profile.traits.map((trait) => traitRow(t, trait))}
          </ul>
        </div>`
      : nothing}

    ${instructionList(t, "writing.block.guidance", profile.guidance, "wsb-guidance")}
    ${instructionList(t, "writing.block.avoid", profile.avoid, "wsb-avoid")}

    ${sample
      ? html`<div class="wsb-group wsb-sample">
          <h4 class="wsb-group-title eyebrow" lang="de">${t("writing.block.sample")}</h4>
          <blockquote class="wsb-sample-text" lang="de">${sample.text}</blockquote>
        </div>`
      : nothing}
  </div>`
}
